import {useEffect,useRef,useState} from 'react';
import {CheckCircle2,FileText,FileUp,X} from 'lucide-react';
import {api,ApiError} from '../../api';
import {Button} from '../ui/button';
import type {PolicyChange,PolicyView} from '../../../../src/domain/policy/describe';

export type History={version:string;actor:string;activatedAt:string;source:string};
export type Active={policyId:string;version:string;summary:PolicyView;yaml:string;customers:number;history:History[];canEdit:boolean;
 emailAutonomy:import('../../../../src/domain/policy/autonomy').EmailAutonomy;drafting:{available:boolean;message:string|null}};
type Preview={valid:boolean;errors:string[];warnings:string[];version:string|null;baseVersion:string;diff:PolicyChange[];yaml:string;
 notes:string[];summary:PolicyView|null};
type Imported={text:string;filename:string|null;drafting:'unavailable'|'drafted'|'failed';message:string|null;draft:Preview|null};
const message=(e:unknown)=>e instanceof Error?e.message:'This step could not be completed.';
const steps=['Upload','Draft','Validate','Review','Confirm'] as const;
const changeNames={added:'New',removed:'Removed',changed:'Changed'} as const;

function readFile(file:File){
 return new Promise<string>((resolve,reject)=>{const reader=new FileReader();
  reader.onload=()=>resolve(String(reader.result).split(',')[1]??'');reader.onerror=()=>reject(new Error('The file could not be read.'));
  reader.readAsDataURL(file);});
}

function Column({title,view,status,side}:{title:string;view:PolicyView;status:Map<string,PolicyChange['change']>;side:'before'|'after'}){
 return <div className="pj-diff-col"><h4>{title}</h4><ol>{view.journey.steps.map(s=>{
  const change=status.get(s.label);const first=s.base.actions[0];
  return <li key={s.stageId} data-change={change??'same'}>
   <span className="pj-diff-day">{s.dayLabel}</span>
   <span className="pj-diff-what">{s.label!==s.dayLabel&&!/^Day -?\d+$/.test(s.label)?`${s.label}. `:''}{first?.title}
    {s.base.actions.length>1?` and ${s.base.actions.length-1} more`:''}</span>
   {change&&<span className="pj-diff-badge">{changeNames[change]}<span className="sr-only"> in the {side} version</span></span>}
  </li>;})}</ol></div>;
}

/** Before and after timelines side by side, with changed, new and removed steps marked; other rule changes listed below. */
export function DiffTimeline({before,after,diff,beforeVersion,afterVersion}:{before:PolicyView;after:PolicyView;diff:PolicyChange[];
 beforeVersion:string;afterVersion:string}){
 const status=new Map(diff.filter(d=>d.section.startsWith('Stage ')).map(d=>[d.section.slice(6),d.change]));
 const other=diff.filter(d=>!d.section.startsWith('Stage ')&&d.section!=='Version');
 return <div className="pj-diff">
  <div className="pj-diff-cols"><Column title={`Now, ${beforeVersion}`} view={before} status={status} side="before"/>
   <Column title={`After, ${afterVersion}`} view={after} status={status} side="after"/></div>
  {other.length>0&&<><h4>Other rule changes</h4><table className="policy-diff"><thead><tr><th>Rule</th><th>Now</th><th>After</th></tr>
  </thead><tbody>{other.map(d=><tr key={d.section}><td>{d.section} <em>({d.change})</em></td>
   <td>{d.before??'None'}</td><td>{d.after??'None'}</td></tr>)}</tbody></table></>}
  {diff.length===0&&<p className="muted">No rule changes.</p>}
 </div>;
}

/** Owner: upload or paste, optionally AI-draft, edit YAML, validate, review the diff, then confirm activation. */
export function ImportSheet({open,onClose,active,onActivated}:{open:boolean;
 onClose:()=>void;active:Active;onActivated:(next:Active)=>void}){
 const dialog=useRef<HTMLDialogElement>(null);
 const [file,setFile]=useState<File|null>(null);const [text,setText]=useState('');
 const [imported,setImported]=useState<Imported|null>(null);
 const [yaml,setYaml]=useState(active.yaml);const [preview,setPreview]=useState<Preview|null>(null);
 const [reviewed,setReviewed]=useState(false);
 const [busy,setBusy]=useState('');const [error,setError]=useState('');const [done,setDone]=useState('');
 useEffect(()=>{const el=dialog.current;if(!el)return;if(open&&!el.open)el.showModal();if(!open&&el.open)el.close();},[open]);
 async function run(label:string,work:()=>Promise<void>){setBusy(label);setError('');setDone('');
  try{await work();}catch(e){setError(message(e));}finally{setBusy('');}}
 const edit=(next:string)=>{setYaml(next);setPreview(null);setReviewed(false);};
 const extract=()=>run('extract',async()=>{
  const body=file?{filename:file.name,contentBase64:await readFile(file)}:{text};
  const result=await api<Imported>(`/app-api/policies/${active.policyId}/import`,body);setImported(result);
  if(result.draft){setYaml(result.draft.yaml);setPreview(result.draft);setReviewed(false);}
 });
 const validate=()=>run('validate',async()=>{
  setPreview(await api<Preview>(`/app-api/policies/${active.policyId}/preview`,{yaml}));setReviewed(false);});
 const activate=()=>run('activate',async()=>{
  const question=`Activate ${preview?.version}? New reminders follow it; flows already running keep their version.`;
  if(!preview?.valid||!window.confirm(question))return;
  try{const next=await api<Active>(`/app-api/policies/${active.policyId}/activate`,{yaml,baseVersion:preview.baseVersion,confirm:true});
   setDone(`Version ${next.version} is active.`);setPreview(null);setReviewed(false);setImported(null);onActivated(next);}
  catch(e){if(e instanceof ApiError&&e.status===409)setPreview(null);throw e;}
 });
 const current=reviewed?4:preview?.valid?3:preview?2:imported?1:0;
 return <dialog ref={dialog} className="pj-sheet" aria-labelledby="pj-sheet-title" onClose={onClose}
  onClick={e=>{if(e.target===dialog.current)onClose();}}>
  <div className="pj-sheet-inner">
   <header className="pj-sheet-head"><div><h2 id="pj-sheet-title">Import a policy</h2>
    <p>Nothing changes until you confirm. Running flows keep the version they started on.</p></div>
    <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}><X/></Button></header>
   <ol className="pj-steps" aria-label="Progress">{steps.map((s,i)=>{
    const state=i<current?(i<2&&!imported?'skipped':'done'):i===current?'now':'next';
    return <li key={s} data-state={state} aria-current={i===current?'step':undefined}>
     <span>{state==='done'?<CheckCircle2 size={13} aria-hidden/>:i+1}</span>{s}
     {state==='skipped'&&<span className="sr-only"> (skipped)</span>}</li>;})}</ol>

   <section className="pj-sheet-section" aria-labelledby="pj-upload-title"><h3 id="pj-upload-title">Upload the written policy</h3>
    {!active.drafting.available&&<div className="alert notice">{active.drafting.message}. You can still upload to read the text and
     edit the rules by hand below.</div>}
    <label className="pj-drop"><FileUp size={20} aria-hidden/><span><strong>{file?file.name:'Choose a policy PDF'}</strong>
     <small>Its content is read as data, never as instructions.</small></span>
     <input type="file" accept="application/pdf" onChange={e=>{setFile(e.target.files?.[0]??null);setImported(null);}}/></label>
    <details className="pj-paste"><summary>Or paste the policy text</summary>
     <textarea rows={4} value={text} disabled={Boolean(file)} onChange={e=>setText(e.target.value)} aria-label="Policy text"/></details>
    <Button disabled={Boolean(busy)||(!file&&!text.trim())} onClick={()=>void extract()}>
     {busy==='extract'?'Reading…':active.drafting.available?'Read and draft rules':'Read the document'}</Button>
    {imported&&<>{imported.message&&<div className={`alert ${imported.drafting==='failed'?'error':'notice'}`}>{imported.message}</div>}
     <details open={!imported.draft}><summary><FileText size={15} aria-hidden/> Extracted text
      {imported.filename?` from ${imported.filename}`:''}</summary><pre className="policy-text">{imported.text}</pre></details></>}
   </section>

   <section className="pj-sheet-section" aria-labelledby="pj-yaml-title"><h3 id="pj-yaml-title">Check the rules</h3>
    <p className="muted">{imported?.draft?'The AI draft is loaded below. Review and edit it before validating.'
     :'Starts from the active policy. Edit it here, or upload a document above.'}</p>
    <details className="pj-paste" open={Boolean(imported?.draft)}><summary>Edit the rules as YAML</summary>
     <textarea className="policy-yaml" rows={14} spellCheck={false} value={yaml} onChange={e=>edit(e.target.value)}
      aria-label="Policy YAML"/></details>
    <div className="resolution-buttons"><Button variant="outline" disabled={Boolean(busy)} onClick={()=>void validate()}>
     {busy==='validate'?'Validating…':'Validate and show changes'}</Button>
     <Button variant="ghost" disabled={Boolean(busy)} onClick={()=>edit(active.yaml)}>Reset to active</Button></div>
   </section>

   {preview&&<section className="pj-sheet-section" aria-labelledby="pj-review-title"><h3 id="pj-review-title">Review the changes</h3>
    {!preview.valid?<div className="alert error" role="alert"><strong>This policy cannot be activated.</strong>
     <ul>{preview.errors.slice(0,50).map((e,i)=><li key={i}>{e}</li>)}</ul></div>:<>
     <p className="pj-valid"><CheckCircle2 size={16} aria-hidden/> Valid. It would become version <strong>{preview.version}</strong>,
      replacing {preview.baseVersion}.</p>
     {preview.warnings.length>0&&<ul className="muted">{preview.warnings.map((w,i)=><li key={i}>{w}</li>)}</ul>}
     {preview.notes.length>0&&<><h4>Drafting notes</h4><ul>{preview.notes.map((n,i)=><li key={i}>{n}</li>)}</ul></>}
     {preview.summary&&<DiffTimeline before={active.summary} after={preview.summary} diff={preview.diff}
      beforeVersion={preview.baseVersion} afterVersion={preview.version??'new'}/>}
     <label className="checkbox pj-confirm"><input type="checkbox" checked={reviewed} onChange={e=>setReviewed(e.target.checked)}/>
      I reviewed these changes. Activate them as a new policy version.</label>
     <Button disabled={Boolean(busy)||!reviewed} onClick={()=>void activate()}>
      {busy==='activate'?'Activating…':`Activate ${preview.version}`}</Button></>}
   </section>}
   {error&&<div className="alert error" role="alert">{error}</div>}
   {done&&<p className="success-message" role="status"><CheckCircle2 size={17}/> {done}</p>}
  </div>
 </dialog>;
}
